// Storefront "You own this" / "View in Library" badges. Reads
// /apps/wrpg/ownership for the product GIDs currently in the DOM, then
// paints a badge on product cards (any owned variant) and on the product
// page (the currently selected variant). Shop purchases are green; variants
// the customer marked "I already have this" are yellow.
(() => {
  if (window.__wrpgOwnershipLoaded) return;
  window.__wrpgOwnershipLoaded = true;

  const ENDPOINT = "/apps/wrpg/ownership";
  const FOCUS_ENDPOINT = "/apps/wrpg/downloads-focus";
  const DOWNLOADS_PAGE_UUID = "01a0330c-7321-72d0-8034-6e6ab781e06f";
  const shopOwnedByProduct = new Map();
  const selfReportedByProduct = new Map();
  let loggedIn = false;

  function productGidsOnPage() {
    const ids = new Set();
    document.querySelectorAll("[data-product-id]").forEach((node) => {
      if (node.dataset.productId) ids.add(node.dataset.productId);
    });
    return [...ids];
  }

  function accountUrl() {
    const root = document.querySelector("[data-wrpg-account-url]");
    return (root && root.dataset.wrpgAccountUrl) || "/account";
  }

  function downloadsBase() {
    const root = document.querySelector("[data-wrpg-downloads-url]");
    const raw = root && root.dataset.wrpgDownloadsUrl;
    if (raw && String(raw).trim()) return String(raw).trim();
    const shopId = window.Shopify && window.Shopify.shopId;
    if (shopId) return `https://shopify.com/${shopId}/account/pages/${DOWNLOADS_PAGE_UUID}`;
    return `https://shopify.com/account/pages/${DOWNLOADS_PAGE_UUID}`;
  }

  function downloadsUrl() {
    const base = downloadsBase();
    try {
      const url = new URL(base, window.location.origin);
      url.search = "";
      url.hash = "";
      return url.href;
    } catch {
      return String(base).split("?")[0].split("#")[0];
    }
  }

  function setBadgeHref(badge) {
    if (!badge || badge.tagName !== "A") return;
    badge.setAttribute("href", downloadsUrl());
  }

  function mergeIds(productId) {
    return [...(shopOwnedByProduct.get(productId) || []), ...(selfReportedByProduct.get(productId) || [])];
  }

  function paintCardBadges() {
    document.querySelectorAll("[data-wrpg-own-badge]").forEach((badge) => {
      const productId = badge.dataset.productId;
      const shop = shopOwnedByProduct.get(productId) || [];
      const self = selfReportedByProduct.get(productId) || [];
      badge.hidden = shop.length === 0 && self.length === 0;
      badge.classList.toggle("wrpg-own-badge--self", shop.length === 0 && self.length > 0);
      setBadgeHref(badge);
    });
  }

  function currentVariantGid() {
    const input = document.querySelector('product-form [name="id"], .product-form [name="id"], input.product-variant-id');
    if (!input || !input.value) return null;
    const raw = String(input.value);
    return raw.startsWith("gid://") ? raw : `gid://shopify/ProductVariant/${raw}`;
  }

  function paintProductPage() {
    const badges = document.querySelectorAll("[data-wrpg-own-product], [data-wrpg-own-gallery]");
    if (badges.length === 0) return;
    badges.forEach((badge) => {
      const productId = badge.dataset.productId;
      const shop = shopOwnedByProduct.get(productId) || [];
      const self = selfReportedByProduct.get(productId) || [];
      const variantId = currentVariantGid();
      const shopHit = variantId ? shop.includes(variantId) : shop.length > 0;
      const selfHit = variantId ? self.includes(variantId) : self.length > 0;
      badge.hidden = !shopHit && !selfHit;
      badge.classList.toggle("wrpg-own-badge--self", !shopHit && selfHit);
      setBadgeHref(badge);
    });
  }

  function paintOptionOwnedChips() {
    const shop = new Set();
    const self = new Set();
    if (loggedIn) {
      shopOwnedByProduct.forEach((ids) => (ids || []).forEach((id) => shop.add(id)));
      selfReportedByProduct.forEach((ids) => (ids || []).forEach((id) => self.add(id)));
    }
    document.querySelectorAll("[data-wrpg-option-owned]").forEach((chip) => {
      const gids = String(chip.dataset.variantGids || "")
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);
      const shopHit = gids.some((gid) => shop.has(gid));
      const selfHit = gids.some((gid) => self.has(gid));
      chip.hidden = !shopHit && !selfHit;
      chip.classList.toggle("wrpg-option-owned--self", !shopHit && selfHit);
    });
  }

  function fetchOwnership() {
    const ids = productGidsOnPage();
    if (ids.length === 0) return Promise.resolve();

    const chunks = [];
    for (let i = 0; i < ids.length; i += 40) chunks.push(ids.slice(i, i + 40));

    return Promise.all(
      chunks.map((chunk) =>
        fetch(`${ENDPOINT}?productIds=${encodeURIComponent(chunk.join(","))}`)
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null),
      ),
    ).then((pages) => {
      pages.forEach((data) => {
        if (!data) return;
        loggedIn = Boolean(data.loggedIn);
        Object.entries(data.owned || {}).forEach(([productId, variantIds]) => {
          shopOwnedByProduct.set(productId, variantIds);
        });
        Object.entries(data.selfReported || {}).forEach(([productId, variantIds]) => {
          selfReportedByProduct.set(productId, variantIds);
        });
      });
      paintCardBadges();
      paintProductPage();
      paintOptionOwnedChips();
    });
  }

  function paintProductCards() {
    if (window.wrpgWishlist && typeof window.wrpgWishlist.sync === "function") {
      window.wrpgWishlist.sync();
    }
    if (window.wrpgExclusions && typeof window.wrpgExclusions.sync === "function") {
      window.wrpgExclusions.sync();
    }
    return fetchOwnership();
  }

  let gridPaintTimer = 0;
  function observeProductGrid() {
    const root = document.getElementById("ProductGridContainer");
    if (!root || root.dataset.wrpgPaintObserved === "true") return;
    root.dataset.wrpgPaintObserved = "true";
    const observer = new MutationObserver(() => {
      window.clearTimeout(gridPaintTimer);
      gridPaintTimer = window.setTimeout(() => {
        paintProductCards();
      }, 50);
    });
    observer.observe(root, { childList: true });
  }

  document.addEventListener("change", (event) => {
    if (event.target && (event.target.name === "id" || event.target.closest("variant-selects"))) {
      paintProductPage();
    }
  });

  document.addEventListener("click", (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    const target = event.target;
    if (!target || typeof target.closest !== "function") return;
    const badge = target.closest("a[data-wrpg-own-badge], a[data-wrpg-own-product], a[data-wrpg-own-gallery]");
    if (!badge) return;
    const productId = badge.dataset.productId;
    if (!productId) return;
    event.preventDefault();
    const href = badge.getAttribute("href") || downloadsUrl();
    fetch(FOCUS_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ productId }),
    })
      .catch(() => null)
      .then(() => {
        window.location.assign(href);
      });
  });

  function boot() {
    observeProductGrid();
    fetchOwnership();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  window.wrpgPaintProductCards = paintProductCards;
  window.wrpgOwnership = {
    ready: fetchOwnership,
    isLoggedIn: () => loggedIn,
    getOwnedVariantIds: (productId) => mergeIds(productId),
    getShopOwnedVariantIds: (productId) => shopOwnedByProduct.get(productId) || [],
    getSelfReportedVariantIds: (productId) => selfReportedByProduct.get(productId) || [],
    sync: () => {
      paintCardBadges();
      paintProductPage();
      paintOptionOwnedChips();
    },
    accountUrl,
    downloadsUrl,
  };
})();
